import React, { useState, useEffect } from 'react';
import { MessageSquare, LogOut, Bot, Settings, MessageSquareQuote, Edit3, Headphones, Ban } from 'lucide-react';
import { useAuth, useSettings } from '../../App';
import request from '../../api/request';
import UserList from '../sidebar/UserList';
import ChatWindow from '../chat/ChatWindow';

import SettingsView from '../settings/SettingsView';
import QuickReplyManager from '../settings/QuickReplyManager';
import { User } from '../../types';
import { useWebSocket } from '../../hooks/useWebSocket';
import AiPromptManager from '../settings/AiPromptManager';
import OrderAnalysisSettingsModal from '../settings/OrderAnalysisSettingsModal';
import ExcludedOrdersModal from '../sidebar/ExcludedOrdersModal';

const MainLayout: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const { settings, setSettings, updateSettingsApi } = useSettings();
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [activeView, setActiveView] = useState<'chat' | 'settings' | 'replies' | 'ai-prompts'>('chat');

  const { isConnected } = useWebSocket();
  const [isAiTakeoverGlobal, setIsAiTakeoverGlobal] = useState(settings?.isAiTakeoverGlobal ?? false);
  const [isOrderAnalysisEnabled, setIsOrderAnalysisEnabled] = useState(true);
  const [orderAnalysisModalOpen, setOrderAnalysisModalOpen] = useState(false);
  const [excludedOrdersModalOpen, setExcludedOrdersModalOpen] = useState(false);

  // 加载客服跟进开关状态
  useEffect(() => {
    const loadOrderAnalysis = async () => {
      try {
        const response = await request.get('/api/settings');
        setIsOrderAnalysisEnabled(response.data.orderAnalysisEnabled ?? true);
      } catch (e) {
        console.error('加载客服跟进设置失败:', e);
      }
    };
    loadOrderAnalysis();
  }, []);

  useEffect(() => {
    const handleApiError = (event: Event) => {
      const customEvent = event as CustomEvent;
      const error = customEvent.detail;
      // console.warn('[MainLayout] Global API Error:', error);
    };

    const handleSendError = (event: Event) => {
      const customEvent = event as CustomEvent;
      const error = customEvent.detail;
      // console.warn('[MainLayout] Send Error:', error);
    };

    window.addEventListener('api_error', handleApiError);
    window.addEventListener('send_error', handleSendError);

    return () => {
      window.removeEventListener('api_error', handleApiError);
      window.removeEventListener('send_error', handleSendError);
    };
  }, []);

  useEffect(() => {
    if (settings) {
      setIsAiTakeoverGlobal(settings.isAiTakeoverGlobal);
    }
  }, [settings]);

  const handleSelectUser = (user: User) => {
    setSelectedUser(user);
  };

  const handleBackToList = () => {
    setSelectedUser(null);
  };

  // 桌面端导航项
  const NavItemDesktop: React.FC<{
    icon: React.ElementType;
    label: string;
    isActive: boolean;
    onClick: () => void;
  }> = ({ icon: Icon, label, isActive, onClick }) => (
    <button
      onClick={onClick}
      aria-label={label}
      className={`relative flex flex-col items-center justify-center w-full h-14 transition-all duration-200 ${isActive ? 'text-accent' : 'text-muted-foreground hover:text-foreground'
        }`}
    >
      <Icon className="w-5 h-5 mb-0.5" />
      <span className="text-[10px]">{label}</span>
      {isActive && (
        <div className="absolute left-0 w-1 h-6 bg-accent rounded-r-full" />
      )}
    </button>
  );

  // 移动端导航项 - 优化触摸响应
  const NavItemMobile: React.FC<{
    icon: React.ElementType;
    label: string;
    isActive: boolean;
    onClick: () => void;
  }> = ({ icon: Icon, label, isActive, onClick }) => {
    const handleClick = (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onClick();
    };

    return (
      <button
        type="button"
        onClick={handleClick}
        onTouchEnd={handleClick}
        aria-label={label}
        className={`flex flex-col items-center justify-center py-2 px-1 min-w-0 touch-manipulation active:opacity-70 ${isActive ? 'text-accent' : 'text-muted-foreground'
          }`}
        style={{ WebkitTapHighlightColor: 'transparent' }}
      >
        <Icon className="w-5 h-5 mb-0.5" />
        <span className="text-[9px] truncate">{label}</span>
      </button>
    );
  };

  // 移动端聊天视图 - 只显示 UserList 或 ChatWindow
  const renderMobileChatView = () => {
    if (selectedUser) {
      return <ChatWindow user={selectedUser} onBack={handleBackToList} />;
    }
    return <UserList onSelectUser={handleSelectUser} selectedUserId={null} />;
  };

  // 桌面端聊天视图 - 同时显示 UserList 和 ChatWindow
  const renderDesktopChatView = () => (
    <>
      <UserList onSelectUser={handleSelectUser} selectedUserId={selectedUser?.id ?? null} />
      <ChatWindow user={selectedUser} onBack={handleBackToList} />
    </>
  );

  const renderContent = () => {
    switch (activeView) {
      case 'chat':
        return (
          <>
            {/* 移动端: 只显示一个 */}
            <div className="md:hidden flex-1 flex flex-col overflow-hidden">
              {renderMobileChatView()}
            </div>
            {/* 桌面端: 同时显示两个 */}
            <div className="hidden md:flex flex-1 overflow-hidden">
              {renderDesktopChatView()}
            </div>
          </>
        );

      case 'settings': return <SettingsView />;
      case 'replies': return <QuickReplyManager />;
      case 'ai-prompts': return <AiPromptManager />;
      default: return null;
    }
  };

  return (
    <div className="flex flex-col md:flex-row h-screen bg-background text-foreground overflow-hidden">
      {/* 桌面端左侧导航栏 */}
      <nav className="hidden md:flex w-16 lg:w-20 bg-card border-r border-border flex-col items-center justify-between py-2 flex-shrink-0">
        <div className="flex flex-col items-center w-full">
          <NavItemDesktop icon={MessageSquare} label="聊天" isActive={activeView === 'chat'} onClick={() => setActiveView('chat')} />

          <NavItemDesktop icon={Settings} label="设置" isActive={activeView === 'settings'} onClick={() => setActiveView('settings')} />
          <NavItemDesktop icon={MessageSquareQuote} label="回复" isActive={activeView === 'replies'} onClick={() => setActiveView('replies')} />
          <NavItemDesktop icon={Edit3} label="AI" isActive={activeView === 'ai-prompts'} onClick={() => setActiveView('ai-prompts')} />
          <NavItemDesktop icon={Ban} label="排除订单" isActive={false} onClick={() => setExcludedOrdersModalOpen(true)} />
        </div>
        <div className="flex flex-col items-center w-full">
          <button
            onClick={async () => {
              const next = !isAiTakeoverGlobal;
              setIsAiTakeoverGlobal(next);
              try {
                const updated = await updateSettingsApi({ isAiTakeoverGlobal: next });
                setSettings(updated as any);
              } catch (e) {
                setIsAiTakeoverGlobal(!next);
              }
            }}
            className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 transition-colors ${isAiTakeoverGlobal ? 'bg-accent text-accent-foreground' : 'bg-muted text-muted-foreground'
              }`}
            title={isAiTakeoverGlobal ? 'AI接管开启' : 'AI接管关闭'}
          >
            <Bot className="w-5 h-5" />
          </button>
          {/* 客服跟进设置弹窗 */}
          <button
            onClick={() => setOrderAnalysisModalOpen(true)}
            className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 transition-colors ${isOrderAnalysisEnabled ? 'bg-accent text-accent-foreground' : 'bg-muted text-muted-foreground'
              }`}
            title="客服跟进设置"
          >
            <Headphones className="w-5 h-5" />
          </button>
          <NavItemDesktop icon={LogOut} label="登出" isActive={false} onClick={logout} />
        </div>
      </nav>

      {/* 主内容区域 */}
      <main className="flex-1 flex flex-col md:flex-row overflow-hidden">
        {renderContent()}
      </main>

      {/* 移动端底部导航栏 - 显示所有按钮 */}
      <nav
        className="md:hidden grid grid-cols-6 bg-card border-t border-border flex-shrink-0"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <NavItemMobile icon={MessageSquare} label="聊天" isActive={activeView === 'chat'} onClick={() => { setActiveView('chat'); setSelectedUser(null); }} />

        <NavItemMobile icon={Settings} label="设置" isActive={activeView === 'settings'} onClick={() => setActiveView('settings')} />
        <NavItemMobile icon={MessageSquareQuote} label="回复" isActive={activeView === 'replies'} onClick={() => setActiveView('replies')} />
        <NavItemMobile icon={Edit3} label="AI" isActive={activeView === 'ai-prompts'} onClick={() => setActiveView('ai-prompts')} />
        <NavItemMobile icon={Ban} label="排除" isActive={false} onClick={() => setExcludedOrdersModalOpen(true)} />
        <NavItemMobile icon={LogOut} label="登出" isActive={false} onClick={logout} />
      </nav>

      {/* 订单分析设置弹窗 */}
      <OrderAnalysisSettingsModal
        isOpen={orderAnalysisModalOpen}
        onClose={() => setOrderAnalysisModalOpen(false)}
        onSettingsChange={(enabled: boolean) => setIsOrderAnalysisEnabled(enabled)}
      />
      {/* 排除订单弹窗 */}
      {excludedOrdersModalOpen && (
        <ExcludedOrdersModal onClose={() => setExcludedOrdersModalOpen(false)} />
      )}
    </div>
  );
};

export default MainLayout;
